












































import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import { navigatorCurrentPosition } from '@/helpers/GeolocationHelper';
import handleResizeWithDebounce from '@/helpers/windowResizeHandlerWithDebounce';

import meQuery from '@/graphql/me/Me.query.gql';
import appBaseContentQuery from '@/graphql/marketingSite/AppBaseContent.query.gql';

import AppLoaderScreen from '@/components/AppLoaderScreen.vue';
import AppHeader from '@/components/partials/AppHeader.vue';
import AppMainMenu from '@/components/AppMainMenu.vue';
import MsgInbox from '@/components/partials/profile/MsgInbox.vue';
import PermissionsModal from '@/components/partials/PermissionsModal.vue';
import InboxMsgIcon from '@/components/icons/InboxMsgIcon.vue';
import TheSearchIcon from '@/components/icons/TheSearchIcon.vue';
import RenewYourMembershipModal from '@/components/modals/RenewYourMembershipModal.vue';
import AppDataFilterMain from '@/components/data-filter/AppDataFilterMain.vue';
import AppCookiePolicyPopupController from '@/components/AppCookiePolicyPopupController.vue';
import AppFooter from '@/components/AppFooter.vue';
import { isAuthRoute } from '@/helpers/routerHelper';

let debounceResize: any = handleResizeWithDebounce();

@Component({
  components: {
    AppFooter,
    AppCookiePolicyPopupController,
    AppDataFilterMain,
    AppLoaderScreen,
    InboxMsgIcon,
    AppHeader,
    AppMainMenu,
    MsgInbox,
    PermissionsModal,
    RenewYourMembershipModal,
    TheSearchIcon,
    MemberLiaisonModal: () =>
      import(/* webpackChunkName: "MemberLiaisonModal" */ '@/components/partials/elements/MemberLiaisonModal.vue'),
  },
  metaInfo: {
    titleTemplate: '%s - The Cultivist Members',
  },
  computed: {
    ...mapState([
      'isAuth',
      'isMainMenuShown',
      'isMobileScreen',
      'isMsgInboxShown',
      'isPermissionsModalShown',
      'showLoaderScreen',
      'unreadAppMessagesCount',
      'user',
    ]),
    ...mapGetters(['isUserMembershipLapsed']),
    isRenewMembershipModalShown() {
      return this.$store.state.renewMembershipModal.isModalShown;
    },
  },
  apollo: {
    me: {
      query: meQuery,
      skip() {
        return !this.isAuth;
      },
      variables() {
        return {
          withSubscription: true,
        };
      },
      result({ data }: any) {
        if (data?.me) {
          this.$store.dispatch('setUser', data.me);

          const unreadMessagesCount = data.me.unreadAppMessagesCount || 0;
          this.$store.dispatch('setUnreadAppMessagesCount', unreadMessagesCount);

          if (data.me.membership) {
            this.$store.dispatch('setUserMembership', { ...data.me.membership });
          }
        }
      },
    },
    appBaseContent: {
      query: appBaseContentQuery,
      skip() {
        return !this.isAuth;
      },
      update: (data: any) => data,
    },
  },
})
export default class AppDefaultLayout extends Vue {
  isMainMenuShown: boolean;
  isMobileScreen: boolean;
  isMsgInboxShown: boolean;
  isUserMembershipLapsed: boolean;
  scrolled = false;
  firstPath = '';
  hasFixedTabs = false;
  isMobileHeader = false;
  appBaseContent = {
    footerNavLinks: [],
    appSocials: [],
  };

  get authRoute() {
    return isAuthRoute(this.$route.name);
  }

  get isMemberLiaisonModalShown() {
    return this.$store.state.memberLiaisonModal.isModalShown;
  }

  get liaison() {
    return this.$store.state.user?.liaison;
  }

  @Watch('$route')
  onRouteChange() {
    this.hideMainMenu();
    this.isMobileHeader = !!document.querySelector('.with-mobile-header');
  }

  @Watch('isMobileScreen')
  watchIsMobileScreen(isMobileScreen: boolean) {
    if (isMobileScreen && this.isMainMenuShown) {
      this.$store.dispatch('disableScroll');
    } else {
      this.$store.dispatch('enableScroll');
    }
  }

  @Watch('isUserMembershipLapsed', { immediate: true })
  watchIsUserMembershipLapsed(isUserMembershipLapsed: boolean) {
    this.$store.dispatch('renewMembershipModal/toggleModal', isUserMembershipLapsed);
  }

  created() {
    this.hideMsgInbox();
    this.hideMainMenu();
    this.$store.dispatch('filter/toggleFilter', false);
    this.$store.dispatch('memberLiaisonModal/toggleModal', false);

    navigatorCurrentPosition(
      () => {
        this.$store.dispatch('showOrHidePermissionsModal', false);
      },
      () => {
        this.$store.dispatch('showOrHidePermissionsModal', true);
      }
    );

    window.addEventListener('scroll', this.handleScroll);

    this.firstPath = this.$route.path;

    this.handleReadMoreBtn();

    this.determineIsMobileScreen();
    window.addEventListener('resize', this.handleResize);
  }

  updated() {
    if (this.$route.path !== this.firstPath) {
      this.firstPath = this.$route.path;
    }
    this.handleReadMoreBtn();
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    debounceResize = null;
    this.$store.dispatch('showLoaderScreen', false);
  }

  determineIsMobileScreen() {
    this.$store.dispatch('determineIsMobileScreen', window.innerWidth);
  }

  handleCloseRenewYourMembershipModal() {
    this.$store.dispatch('renewMembershipModal/toggleModal', false);
  }

  handleResize() {
    if (debounceResize !== null) {
      debounceResize(() => {
        this.determineIsMobileScreen();
        this.handleReadMoreBtn();
      });
    }
  }

  handleScroll() {
    this.scrolled = window.scrollY > 0;
  }

  handleReadMoreBtn() {
    if (this.$route.name === 'insider-tip') {
      return;
    }

    const readMoreButtons = document.querySelectorAll('.read-more');

    for (let i = 0; i < readMoreButtons.length; i++) {
      const btn = readMoreButtons[i] as HTMLElement;

      const desc = btn.previousElementSibling as HTMLElement;
      if (!desc) {
        return;
      }

      const descInner = desc.querySelector('.desc__inner') as HTMLElement;
      if (!descInner) {
        return;
      }

      if (descInner.offsetHeight > desc.offsetHeight) {
        btn.style.display = 'block';
        desc.classList.remove('without-shadow');
      } else {
        btn.style.display = 'none';
        desc.classList.add('without-shadow');
      }
    }
  }

  showMsgInbox() {
    this.$store.dispatch('showMsgInbox', true);
  }

  hideMsgInbox() {
    this.$store.dispatch('showMsgInbox', false);
  }

  hideMainMenu() {
    this.$store.dispatch('showMainMenu', false);
  }

  logout() {
    this.hideMainMenu();
    this.$store.dispatch('logout').then(() => {
      this.$router.push({ name: 'login' });
    });
  }

  handleCloseLiaisonModal() {
    this.$store.dispatch('memberLiaisonModal/toggleModal', false);
  }
}
